import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BaseEdge,
  EdgeLabelRenderer,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Layers } from 'lucide-react';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import type { AccountingPeriod, ClosureTone } from '@/versions/v1/schema/metal-accounting';
import type { Stream } from '@/versions/v1/schema/stream';
import type { ScenarioPhase, ScenarioId, SessionState } from '@/versions/v1/schema/scenario';
import { SCENARIOS, isWhatIfDefault } from '@/versions/v1/shared/ScenarioEngine';
import type { SamplePoint } from '@/versions/v1/schema/unit-operation';
import { type ResolvedUtilizationRank } from './Scene.transform';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import type { RollupLens } from '@/versions/v1/shared/rollup-lens';
import {
  GRAPH_VIEWBOX,
  metalAccountingProcessGraph,
  maLockedBoundsTop,
  maLockedCanvasHeight,
  maLockedFillHeight,
  maLockedZoom,
  processGraph,
  WHAT_IF_BOUNDS,
  WHAT_IF_CAPTION,
  WHAT_IF_DEFAULTS,
  whatIfDerived,
  type ExternalStubView,
  type GraphEdgeView,
  type GraphLoopArc,
  type GraphLoopLabel,
  type GraphNodeView,
  type WhatIfInputs,
} from './ProcessGraph.transform';
import { MetalAccountingStrip } from './MetalAccountingStrip';
import {
  metalAccountingLayer,
  overlayMetalAccountingOnEdges,
  periodWindows,
  type PeriodView,
} from './MetalAccountingLayer.transform';
import { RollupStrip } from './RollupStrip';
import { gmRollupFromViews } from './RollupStrip.transform';
import { BRACKET_H, circuitBrackets, type CircuitBracketView, type FlowCell } from './PlantFlowStrip.transform';
import { UnitOpGlyph, UNIT_OP_TYPE_LABEL } from './UnitOpGlyph';

type ProcessGraphProps = {
  ranks: readonly ResolvedUtilizationRank[];
  phase: ScenarioPhase;
  state: SessionState;
  onPhaseChange: (phase: ScenarioPhase) => void;
  /** Unused since polish.P11 moved Push silver into the plant model header; kept for callers. */
  onToggleScenario?: (id: ScenarioId) => void;
  onSetWhatIf?: (inputs: WhatIfInputs | null) => void;
  samplePoints: readonly SamplePoint[];
  projectSlug: string;
  lens: RollupLens;
  selectedNodeId?: string;
  onSelectNode?: (nodeId: string) => void;
  maLocked?: boolean;
  hideMaToggle?: boolean;
  hideMaStripLink?: boolean;
  // Fix round 3 MR-F14/MR-F25/MR-F26/MR-F27: the strip's `ma-chain-gap` cell
  // duplicates `MineToShippedCard`, which only renders on /metal-accounting.
  // True there so the strip omits the cell; false (default) on
  // /plant-model?layer=ma, the only route where the strip is its sole
  // carrier.
  hideMaStripChainGapCell?: boolean;
  /** polish.P9 (#657): `/metal-accounting` draws the roll-up band above its
   *  strip and the closure reads in the strip itself, so the graph drops its
   *  own copies of both. */
  hideRollup?: boolean;
  hideMaStrip?: boolean;
  /** Sit inside a host panel: no frame of its own around the canvas, and the
   *  canvas fills the panel's height (the graph centred in it) when the host
   *  row is taller than the graph. */
  bare?: boolean;
  /** Right end of the legend row (the host's period window). */
  legendAside?: ReactNode;
  period?: AccountingPeriod;
  view?: PeriodView;
  /** polish.P11: the plant model's strip cells; the circuits among them draw
   *  as brackets over their nodes (B's detail), in the strip's vocabulary. */
  circuitCells?: readonly FlowCell[];
};

function closureChipColor(tone: ClosureTone): string | undefined {
  if (tone === 'unmeasured') return undefined;
  if (tone === 'warn') return vizToneColor('warn');
  if (tone === 'bad') return vizToneColor('bad');
  return vizToneColor('ok');
}

// Selection is owned by the Scene (`selectedNodeId`), never by React Flow's own
// selection store — mirroring the Scene's flag back through `selected` +
// `onSelectionChange` produced a two-node ping-pong (Miles, 2026-08-27).
// Keyboard activation therefore lives on the node's own element.
type SubprocessNodeData = GraphNodeView & {
  selected: boolean;
  onSelect?: (id: string) => void;
  /** The plant-model route's cards (the MA-locked cards stay compacted): sub-lines
   *  wrap instead of truncating, sample chips pin to one line (an off-routine
   *  chip reads its id and word, the stream moves to the tooltip), and the
   *  selected card takes the accent-selected wash (DESIGN.md § Strip and stepper). */
  plantRoute: boolean;
};
type CircuitBracketNodeData = CircuitBracketView & { selected: boolean };
type ExternalStubNodeData = ExternalStubView;
type LoopLabelNodeData = GraphLoopLabel;
type ForwardEdgeData = GraphEdgeView & { reducedMotion: boolean };
type RecycleEdgeData = GraphLoopArc & { reducedMotion: boolean; dotCount: number };

const EXTERNAL_SOURCE_BY_STREAM: Record<string, string> = {
  'st-rom': 'external-in',
  'st-makeup': 'external-makeup',
};

const EXTERNAL_TARGET_BY_STREAM: Record<string, string> = {
  'st-pbcake': 'external-cake',
  'st-zncake': 'external-zncake',
  'st-paste': 'external-paste',
};

const LOOP_STREAM_BY_NODE: Record<string, string> = {
  'sp-grind': 'st-cyc-uf',
  'sp-pb': 'st-pb-cln-tail',
  'sp-zn': 'st-zn-cln-tail',
};

const SELECTABLE_NODE_PREFIX = 'sp-';
const GRAPH_CANVAS_H = 430;

function resolveEdgeSource(stream: Stream): string {
  if (stream.from === 'external') return EXTERNAL_SOURCE_BY_STREAM[stream.id] ?? 'external-in';
  return stream.from;
}

function resolveEdgeTarget(stream: Stream): string {
  if (stream.to === 'external') return EXTERNAL_TARGET_BY_STREAM[stream.id] ?? 'external-cake';
  return stream.to;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return reduced;
}

function ExternalStubNode({ data }: NodeProps<Node<ExternalStubNodeData>>) {
  return (
    <>
    {/* React Flow draws an edge only between handles, and the stubs had none,
        so ROM feed → Crush, Thicken → both concentrates, Paste → backfill and
        make-up → Water never rendered. Every stub carries its handles, on the
        locked layout (polish.P9 r2) and the plant-model route (polish.P11 r1). */}
    <Handle type="target" position={Position.Left} id="in" className="process-graph-handle" />
    <Handle type="source" position={Position.Right} id="out" className="process-graph-handle" />
    <div
      className={cn(
        'process-graph-external-stub flex h-full w-full flex-col justify-center rounded-xl border border-dashed border-[var(--glass-border)] bg-[color-mix(in_srgb,var(--glass-surface)_70%,transparent)] py-1',
        data.wrap ? 'px-1.5' : 'px-2',
      )}
    >
      <p className="text-[10px] font-medium leading-tight text-muted">{data.label}</p>
      {data.subLines.filter(Boolean).map((lineText) => (
        <p
          key={lineText}
          className={
            data.wrap
              ? 'text-[10px] font-semibold leading-tight tabular text-ink-primary'
              : 'truncate text-xs tabular text-zinc-700 dark:text-zinc-300'
          }
        >
          {lineText}
        </p>
      ))}
    </div>
    </>
  );
}

function SubprocessNode({ id, data }: NodeProps<Node<SubprocessNodeData>>) {
  const activate = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    data.onSelect?.(id);
  };
  const cellClass = cn(
    'process-graph-node relative h-full w-full rounded-2xl border px-2.5 py-2 text-left transition-colors bg-[var(--glass-surface)]',
    data.selected
      ? data.plantRoute
        ? 'border-[1.5px] border-[var(--color-accent-500)] bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--glass-surface))]'
        : 'border-zinc-900 dark:border-zinc-100'
      : 'border-[var(--glass-border)]',
    // The locked Metal accounting comp draws a state rule on each card top;
    // the Plant model comp draws none (its brackets and strip carry the read).
    data.plantRoute ? '' : data.topToneClass,
    data.utility ? 'border-dashed' : '',
  );

  return (
    <>
      <Handle type="target" position={Position.Left} id="in" className="process-graph-handle" />
      <Handle type="source" position={Position.Right} id="out" className="process-graph-handle" />
      <Handle type="target" position={Position.Bottom} id="water-in" className="process-graph-handle" />
      <Handle type="source" position={Position.Bottom} id="water-out" className="process-graph-handle" />
      <Handle type="target" position={Position.Top} id="recycle-in" className="process-graph-handle" />
      <Handle type="source" position={Position.Top} id="recycle-out" className="process-graph-handle" />
      <div className={cellClass} role="button" tabIndex={0} aria-pressed={data.selected} onKeyDown={activate}>
        {data.unitOpType && !data.maCompact ? (
          <UnitOpGlyph
            type={data.unitOpType}
            className="absolute right-2 top-2 text-muted"
            title={UNIT_OP_TYPE_LABEL[data.unitOpType]}
            aria-hidden
          />
        ) : null}
        <span className="relative z-10 block">
          {/* Title row is a pinned 20 px on every card, so the closure-gap chip
              rides it without changing the card's height (r11 SHOULD-FIX 1:
              the gap was derived for WTD / MTD and dropped, because the chip
              was nested inside the day-only note branch). `pr-4` clears the
              absolutely-positioned unit-op glyph at the card's top-right. */}
          <span className={cn('flex h-[20px] items-center gap-1', data.unitOpType && !data.maCompact ? 'pr-4' : '')}>
            <span className="min-w-0 truncate text-xs font-medium text-zinc-900 dark:text-zinc-100">
              {data.shortName}
            </span>
            {data.maGap ? (
              <span
                className="shrink-0 rounded-full border border-[var(--glass-border)] px-1 text-[10px] font-semibold tabular"
                style={{ color: closureChipColor(data.maGap.tone) ?? 'var(--ink-tertiary)' }}
                title={data.maGap.title}
              >
                {data.maGap.pct}
              </span>
            ) : null}
          </span>
          {data.headline ? (
            <span
              className="mt-0.5 block truncate text-base font-semibold tabular tracking-tight text-zinc-900 dark:text-zinc-100"
              title={data.headline}
            >
              {data.headline}
            </span>
          ) : null}
          {data.maNote ? (
            <span className="mt-1 flex flex-col gap-0.5">
              <span className="flex items-center gap-1">
                <span
                  className="min-w-0 text-[10px] font-medium text-muted"
                  title={data.maNote.title}
                  style={data.maNote.tone === 'neutral' ? undefined : { color: vizToneColor(data.maNote.tone) }}
                >
                  {data.maNote.text}
                </span>
              </span>
              {data.maNote.subText ? (
                <span className="text-[10px] font-medium leading-tight text-muted" title={data.maNote.title}>
                  {data.maNote.subText}
                </span>
              ) : null}
            </span>
          ) : data.sub ? (
            <span
              className={cn(
                'mt-0.5 block text-xs text-muted',
                data.plantRoute ? 'whitespace-pre-line break-words leading-snug' : 'truncate',
              )}
              title={data.sub}
              data-graph-sub={id}
            >
              {data.sub}
            </span>
          ) : null}
          {data.utilizationPct != null ? (
            <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
              <span className={cn('block h-full rounded-full', data.barClass)} style={{ width: `${data.utilizationPct}%` }} />
            </span>
          ) : null}
          {data.bottleneckLabel ? (
            <span
              className={cn(
                'mt-1 block truncate text-[10px] font-semibold uppercase tracking-wide',
                data.isBottleneck ? 'text-[var(--state-warning)]' : 'text-muted',
              )}
            >
              {data.bottleneckLabel}
            </span>
          ) : null}
          {data.interiorEdges.length > 0 || data.interiorOverflow > 0 ? (
            <span className="mt-1 flex flex-col gap-1">
              {data.interiorEdges.map((row) =>
                data.maCompact ? (
                  // The locked layout's chip: a full-width row pinned at two
                  // lines (31 px, the pair of `MA_LOCKED_CHIP_ROW_H`), so the
                  // whole label reads and the adherence flag follows it.
                  <span
                    key={row.streamId}
                    title={row.chipLabel}
                    data-graph-chip={row.streamId}
                    className="flex h-[31px] items-center overflow-hidden rounded-[10px] border bg-[var(--glass-surface)] px-1.5 text-[10px] font-medium leading-[12px] text-zinc-800 dark:text-zinc-200"
                    style={{ borderColor: vizToneColor(row.tone), borderWidth: 1.5 }}
                  >
                    <span className="min-w-0">
                      {row.chipLabel}
                      {row.adherence !== 'REG' ? (
                        <span className="ml-1 whitespace-nowrap font-semibold uppercase" style={{ color: vizToneColor(row.tone) }}>
                          {row.adherence}
                        </span>
                      ) : null}
                    </span>
                  </span>
                ) : data.plantRoute ? (
                  // The plant-model route pins each interior chip to one line
                  // and truncates, naming an off-routine stream by its sample
                  // id so the adherence flag always fits the card.
                  <span
                    key={row.streamId}
                    title={row.chipLabel}
                    data-graph-chip={row.streamId}
                    className="inline-flex h-[22px] max-w-full items-center gap-1 self-start overflow-hidden rounded-full border bg-[var(--glass-surface)] px-1.5 text-[10px] font-medium text-zinc-800 dark:text-zinc-200"
                    style={{ borderColor: vizToneColor(row.tone), borderWidth: 1.5 }}
                  >
                    <span className="min-w-0 truncate">
                      {row.plantChipText}
                    </span>
                    {row.adherence !== 'REG' ? (
                      <span className="shrink-0 font-semibold uppercase" style={{ color: vizToneColor(row.tone) }}>
                        {row.adherence}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span
                    key={row.streamId}
                    title={row.chipLabel}
                    className="inline-flex items-center gap-1 rounded-full border bg-[var(--glass-surface)] px-1.5 text-[10px] font-medium text-zinc-800 dark:text-zinc-200 py-0.5"
                    style={{ borderColor: vizToneColor(row.tone), borderWidth: 1.5 }}
                  >
                    <span>{row.chipLabel}</span>
                    {row.adherence !== 'REG' ? (
                      <span className="uppercase font-semibold" style={{ color: vizToneColor(row.tone) }}>
                        {row.adherence}
                      </span>
                    ) : null}
                  </span>
                ),
              )}
              {data.interiorOverflow > 0 ? (
                <span className="text-[10px] text-muted">+{data.interiorOverflow}</span>
              ) : null}
            </span>
          ) : null}
        </span>
      </div>
    </>
  );
}

const BRACKET_DOT: Record<CircuitBracketView['status'], string> = {
  ok: 'bg-[var(--state-online)]',
  warn: 'bg-[var(--state-warning)]',
  bad: 'bg-[var(--state-blocked)]',
};

/** B's circuit bracket: the circuit's name, SPC dot and drift word over its node. */
function CircuitBracketNode({ data }: NodeProps<Node<CircuitBracketNodeData>>) {
  return (
    <div className="flex h-full w-full cursor-pointer flex-col justify-between" data-graph-bracket={data.nodeId}>
      <span className="flex items-center gap-1.5 whitespace-nowrap px-1 text-[10px] font-semibold uppercase leading-3 tracking-[0.05em] text-ink-primary">
        <span aria-hidden className={cn('size-[7px] shrink-0 rounded-full', BRACKET_DOT[data.status])} />
        {data.label}
        {data.word ? (
          <span className={data.wordTone === 'bad' ? 'text-[var(--state-blocked)]' : 'text-[var(--state-warning)]'}>
            · {data.word}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        className={cn(
          'block h-2 rounded-t-sm border-x border-t',
          data.selected ? 'border-[var(--color-accent-500)] border-x-2 border-t-2' : 'border-[var(--hairline-strong)]',
        )}
      />
    </div>
  );
}

function LoopLabelNode({ data }: NodeProps<Node<LoopLabelNodeData>>) {
  return (
    <div className="pointer-events-none text-left">
      <p className="truncate text-[10px] font-semibold text-ink-primary" title={data.title}>
        {data.title}
      </p>
      <p className="truncate text-[10px] tabular text-muted" title={data.value}>
        {data.value}
      </p>
    </div>
  );
}

function FlowDots({ edge, reducedMotion }: { edge: GraphEdgeView; reducedMotion: boolean }) {
  if (reducedMotion) return null;
  return (
    <>
      {edge.dots.map((dot) => (
        <circle key={dot.id} r={dot.radius} fill={edge.dotColor} opacity={0.55}>
          <animateMotion
            dur={`${dot.durationSec}s`}
            begin={`${dot.beginSec}s`}
            repeatCount="indefinite"
            path={edge.path}
          />
        </circle>
      ))}
    </>
  );
}

function RecycleFlowDots({ arc, reducedMotion }: { arc: GraphLoopArc; reducedMotion: boolean }) {
  const apexMatch = arc.path.match(/^M (-?[\d.]+) (-?[\d.]+)/);
  const startX = apexMatch ? Number.parseFloat(apexMatch[1]) : 0;
  const startY = apexMatch ? Number.parseFloat(apexMatch[2]) : 0;
  return (
    <>
      {Array.from({ length: arc.dotCount }, (_, index) =>
        reducedMotion ? (
          <circle key={index} r={2.25} fill="var(--ink-secondary)" cx={startX - 28} cy={startY - 22} />
        ) : (
          <circle key={index} r={2.25} fill="var(--ink-secondary)">
            <animateMotion
              dur="4.5s"
              begin={`${-(4.5 / arc.dotCount) * index}s`}
              repeatCount="indefinite"
              path={arc.path}
            />
          </circle>
        ),
      )}
    </>
  );
}

function MarkerDefs() {
  return (
    <svg className="process-graph-markers" aria-hidden>
      <defs>
        <marker id="process-graph-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="var(--ink-secondary)" />
        </marker>
        <marker id="process-graph-arrow-soft" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="var(--ink-tertiary)" />
        </marker>
        <marker id="process-graph-arrow-pb" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="var(--circuit-pb)" />
        </marker>
        <marker id="process-graph-arrow-zn" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="var(--circuit-zn)" />
        </marker>
      </defs>
    </svg>
  );
}

function ForwardEdge({ id, data }: EdgeProps<Edge<ForwardEdgeData>>) {
  if (!data) return null;
  const strokeColor = data.dashed
    ? 'var(--ink-tertiary)'
    : data.circuit === 'pb'
      ? 'var(--circuit-pb)'
      : data.circuit === 'zn'
        ? 'var(--circuit-zn)'
        : 'var(--ink-secondary)';
  const markerEnd = data.dashed
    ? 'url(#process-graph-arrow-soft)'
    : data.circuit === 'pb'
      ? 'url(#process-graph-arrow-pb)'
      : data.circuit === 'zn'
        ? 'url(#process-graph-arrow-zn)'
        : 'url(#process-graph-arrow)';
  return (
    <g opacity={data.opacity}>
      <BaseEdge
        id={id}
        path={data.path}
        style={{
          stroke: strokeColor,
          strokeWidth: data.strokeWidth,
          strokeDasharray: data.dashed ? '5 4' : undefined,
        }}
        markerEnd={markerEnd}
        interactionWidth={12}
      >
        <title>{data.tooltipTitle}</title>
      </BaseEdge>
      {data.sampleChips.map((chip) => (
        <circle
          key={`${chip.id}-anchor`}
          cx={chip.dotX}
          cy={chip.dotY}
          r={3.5}
          fill={vizToneColor(chip.tone)}
          stroke="var(--glass-surface)"
          strokeWidth={1.5}
        />
      ))}
      <FlowDots edge={data} reducedMotion={data.reducedMotion} />
    </g>
  );
}

function WaterEdge(props: EdgeProps<Edge<ForwardEdgeData>>) {
  return <ForwardEdge {...props} />;
}

function RecycleEdge({ id, data }: EdgeProps<Edge<RecycleEdgeData>>) {
  if (!data) return null;
  return (
    <g>
      <BaseEdge
        id={id}
        path={data.path}
        // Ink, as the comp draws it: a recirculating load is none of the five
        // crimson uses (DESIGN.md § Crimson).
        style={{ stroke: 'var(--ink-secondary)', strokeWidth: 2 }}
        markerEnd="url(#process-graph-arrow)"
        interactionWidth={12}
      />
      <RecycleFlowDots arc={data} reducedMotion={data.reducedMotion} />
    </g>
  );
}

/** An invisible 1×1 node at the locked graph's top edge (polish.P9 #657):
 *  `fitView` fits node bounds only, and the Pb-concentrate arc and its SA03
 *  chip ride above every card, so without it they were cut off at the top. */
function BoundsMarkerNode() {
  return <span aria-hidden className="block h-px w-px" />;
}

const MA_BOUNDS_NODE_ID = 'ma-bounds-top';

const nodeTypes = {
  subprocess: SubprocessNode,
  externalStub: ExternalStubNode,
  loopLabel: LoopLabelNode,
  circuitBracket: CircuitBracketNode,
  boundsMarker: BoundsMarkerNode,
};

const edgeTypes = {
  forward: ForwardEdge,
  water: WaterEdge,
  recycle: RecycleEdge,
};

function labelStyle(x: number, y: number, anchor: 'start' | 'middle' | 'end' = 'middle'): CSSProperties {
  const translateX = anchor === 'start' ? '0%' : anchor === 'end' ? '-100%' : '-50%';
  return {
    position: 'absolute',
    transform: `translate(${translateX}, -50%) translate(${x}px, ${y}px)`,
    pointerEvents: 'all',
  };
}

function EdgeLabels({ edges }: { edges: readonly GraphEdgeView[] }) {
  return (
    <EdgeLabelRenderer>
      {edges.map((edge) => (
        <Fragment key={edge.stream.id}>
          {edge.valueLabel ? (
            <div
              style={labelStyle(edge.valueLabel.x, edge.valueLabel.y, edge.valueLabel.anchor)}
              className="nodrag nopan text-[10px] tabular text-[var(--ink-secondary)]"
            >
              {edge.valueLabel.text}
            </div>
          ) : null}
          {edge.maLabels.map((label) => (
            <div
              key={`${label.x}-${label.y}-${label.text}`}
              style={labelStyle(label.x, label.y, label.anchor)}
              className="nodrag nopan text-[10px] font-semibold tabular"
              title={label.title}
            >
              <span style={{ color: label.tone === 'neutral' ? 'var(--ink-secondary)' : vizToneColor(label.tone) }}>
                {label.text}
              </span>
            </div>
          ))}
          {edge.sampleChips.map((chip) => (
            <div
              key={chip.id}
              style={labelStyle(chip.x, chip.y, chip.anchor)}
              className="nodrag nopan"
              title={chip.id}
            >
              {/* The adherence word rides inside the chip, after the id, as
                  the comp draws `SA05 LATE` (polish.P11 r2). */}
              <span
                data-edge-sample-chip={chip.id}
                className="inline-flex min-w-[30px] items-center justify-center gap-1 whitespace-nowrap rounded-full border bg-[var(--glass-surface)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--ink-primary)]"
                style={{ borderColor: vizToneColor(chip.tone), borderWidth: 1.5 }}
              >
                {chip.id}
                {chip.adherence !== 'REG' ? (
                  <span className="uppercase" style={{ color: vizToneColor(chip.tone) }}>
                    {chip.label}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </Fragment>
      ))}
    </EdgeLabelRenderer>
  );
}

function toFlowNodes(
  view: ReturnType<typeof processGraph>,
  selectedNodeId?: string,
  onSelect?: (id: string) => void,
  brackets: readonly CircuitBracketView[] = [],
  plantRoute = false,
  boundsTop?: number,
): Node[] {
  const nodes: Node[] = [];

  for (const bracket of brackets) {
    nodes.push({
      id: bracket.id,
      type: 'circuitBracket',
      position: { x: bracket.x, y: bracket.y },
      data: { ...bracket, selected: bracket.nodeId === selectedNodeId },
      draggable: false,
      selectable: false,
      focusable: false,
      style: { width: bracket.w, height: BRACKET_H },
    });
  }

  if (boundsTop !== undefined && view.nodes.length > 0) {
    nodes.push({
      id: MA_BOUNDS_NODE_ID,
      type: 'boundsMarker',
      position: { x: Math.min(...view.nodes.map((node) => node.x)), y: boundsTop },
      data: {},
      draggable: false,
      selectable: false,
      focusable: false,
      style: { width: 1, height: 1 },
    });
  }

  for (const stub of view.externalStubs) {
    nodes.push({
      id: stub.id,
      type: 'externalStub',
      position: { x: stub.x, y: stub.y },
      data: stub,
      draggable: false,
      selectable: false,
      focusable: false,
      style: { width: stub.w, height: stub.h },
    });
  }

  for (const node of view.nodes) {
    nodes.push({
      id: node.id,
      type: 'subprocess',
      position: { x: node.x, y: node.y },
      data: { ...node, selected: node.id === selectedNodeId, onSelect, plantRoute },
      draggable: false,
      // Not RF-selectable: the Scene owns selection (see SubprocessNodeData).
      selectable: false,
      focusable: false,
      style: { width: node.w, height: node.h },
    });
  }

  for (const label of view.loopLabels) {
    nodes.push({
      id: `loop-label-${label.nodeId}`,
      type: 'loopLabel',
      position: { x: label.x, y: label.y },
      data: label,
      draggable: false,
      selectable: false,
      focusable: false,
      style: { width: label.w },
    });
  }

  return nodes;
}

function toFlowEdges(
  view: ReturnType<typeof processGraph>,
  reducedMotion: boolean,
): Edge<ForwardEdgeData | RecycleEdgeData>[] {
  const edges: Edge<ForwardEdgeData | RecycleEdgeData>[] = view.edges.map((edge) => {
    const isWater = edge.dashed;
    const sourceIsSubprocess = edge.stream.from.startsWith('sp-');
    const targetIsSubprocess = edge.stream.to.startsWith('sp-');
    return {
      id: edge.stream.id,
      source: resolveEdgeSource(edge.stream),
      target: resolveEdgeTarget(edge.stream),
      type: isWater ? 'water' : 'forward',
      ...(isWater && sourceIsSubprocess ? { sourceHandle: 'water-out' } : {}),
      ...(isWater && targetIsSubprocess ? { targetHandle: 'water-in' } : {}),
      data: { ...edge, reducedMotion },
      selectable: false,
      focusable: false,
    };
  });

  for (const arc of view.loopArcs) {
    edges.push({
      id: LOOP_STREAM_BY_NODE[arc.nodeId] ?? `loop-${arc.nodeId}`,
      source: arc.nodeId,
      target: arc.nodeId,
      sourceHandle: 'recycle-out',
      targetHandle: 'recycle-in',
      type: 'recycle',
      data: { ...arc, reducedMotion },
      selectable: false,
      focusable: false,
    });
  }

  return edges;
}

function ProcessGraphCanvas({
  view,
  selectedNodeId,
  onSelectNode,
  reducedMotion,
  canvasHeight,
  brackets,
  plantRoute,
  boundsTop,
  fill = false,
  paneHeight,
}: {
  view: ReturnType<typeof processGraph>;
  selectedNodeId?: string;
  onSelectNode?: (nodeId: string) => void;
  reducedMotion: boolean;
  canvasHeight: number;
  brackets?: readonly CircuitBracketView[];
  plantRoute: boolean;
  boundsTop?: number;
  fill?: boolean;
  paneHeight?: number;
}) {
  const { fitView } = useReactFlow();
  const nodes = useMemo(
    () => toFlowNodes(view, selectedNodeId, onSelectNode, brackets, plantRoute, boundsTop),
    [view, selectedNodeId, onSelectNode, brackets, plantRoute, boundsTop],
  );
  const edges = useMemo(() => toFlowEdges(view, reducedMotion), [view, reducedMotion]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fitView({ padding: 0.02, maxZoom: 1, minZoom: 0.2, duration: 0 });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [fitView, nodes, edges, canvasHeight, paneHeight]);

  const onNodeClick = useCallback(
    (_event: MouseEvent, node: Node) => {
      if (node.type === 'circuitBracket') {
        onSelectNode?.((node.data as CircuitBracketNodeData).nodeId);
        return;
      }
      if (!node.id.startsWith(SELECTABLE_NODE_PREFIX)) return;
      onSelectNode?.(node.id);
    },
    [onSelectNode],
  );

  return (
    <div
      className="process-graph-flow w-full"
      style={fill ? { height: '100%', minHeight: canvasHeight } : { height: canvasHeight }}
    >
      <MarkerDefs />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        // paul-fmea.A13 / #278: the wheel scrolls the page on both routes —
        // ctrl-wheel, pinch (`zoomOnPinch` default true) and fit-to-view stay
        // reachable, so zoom is still available deliberately, not by accident
        // on an ordinary scroll past the graph. `zoomOnScroll`/`panOnScroll`
        // alone are not enough: React Flow's own wheel handler
        // (`createZoomOnScrollHandler`, vendored `@xyflow/system`) calls
        // `event.preventDefault()` on every non-ctrl wheel event whenever
        // `preventScrolling` is true (its default) — swallowing the native
        // page scroll even though the zoom itself is already off. Explicit
        // `preventScrolling={false}` is what actually leaves an ordinary
        // wheel event unprevented; a ctrl-held wheel still reaches the
        // zoom filter and zooms, since that branch is unaffected.
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        minZoom={0.2}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        fitView
        className="process-graph-flow__canvas"
      >
        <EdgeLabels edges={view.edges} />
      </ReactFlow>
    </div>
  );
}

function WhatIfSlider({
  label,
  value,
  display,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">{label}</span>
        <span className="tabular text-muted">{display}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number.parseFloat(event.target.value))}
        className="mt-1 w-full"
        style={{ accentColor: 'var(--accent)' }}
      />
    </label>
  );
}

function WhatIfPanel({
  inputs,
  onChange,
  onReset,
  onClose,
}: {
  inputs: WhatIfInputs;
  onChange: (next: WhatIfInputs) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const d = whatIfDerived(inputs);
  const readouts: { label: string; value: string }[] = [
    { label: 'P80', value: `${d.p80Um} µm` },
    { label: 'Circ load', value: `${d.circPct}%` },
    { label: 'Zn rec', value: `${d.znRecPct.toFixed(1)}%` },
    { label: 'Pb rec', value: `${d.pbRecPct.toFixed(1)}%` },
  ];
  return (
    <div className="absolute right-0 top-full z-20 mt-2 w-80 rounded-2xl border border-divider bg-white p-4 shadow-lg dark:bg-zinc-950">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">What-if inputs</p>
        <button type="button" onClick={onClose} className="text-xs text-muted hover:text-zinc-900 dark:hover:text-zinc-100">
          Close
        </button>
      </div>
      <div className="mt-3 space-y-3">
        <WhatIfSlider
          label="Mill feed"
          value={inputs.feedStph}
          display={`${inputs.feedStph} stph`}
          min={WHAT_IF_BOUNDS.feedStph.min}
          max={WHAT_IF_BOUNDS.feedStph.max}
          step={WHAT_IF_BOUNDS.feedStph.step}
          onChange={(feedStph) => onChange({ ...inputs, feedStph })}
        />
        <WhatIfSlider
          label="Ore hardness"
          value={inputs.hardness}
          display={`${inputs.hardness.toFixed(2)}× design`}
          min={WHAT_IF_BOUNDS.hardness.min}
          max={WHAT_IF_BOUNDS.hardness.max}
          step={WHAT_IF_BOUNDS.hardness.step}
          onChange={(hardness) => onChange({ ...inputs, hardness })}
        />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-[var(--glass-border)] pt-3">
        {readouts.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between">
            <dt className="text-[10px] uppercase tracking-wide text-muted">{row.label}</dt>
            <dd className="text-xs font-semibold tabular text-zinc-900 dark:text-zinc-100">{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-[10px] text-muted">Illustrative · not a solver</p>
        <button
          type="button"
          onClick={onReset}
          className="rounded-full border border-[var(--glass-border)] px-2.5 py-0.5 text-xs font-medium text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

export function ProcessGraph({
  ranks,
  phase,
  state,
  onPhaseChange,
  onSetWhatIf,
  samplePoints,
  projectSlug,
  lens,
  selectedNodeId,
  onSelectNode,
  maLocked = false,
  hideMaToggle = false,
  hideMaStripLink = false,
  hideMaStripChainGapCell = false,
  hideRollup = false,
  hideMaStrip = false,
  bare = false,
  legendAside,
  period = 'day',
  view: accountingView,
  circuitCells,
}: ProcessGraphProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const whatIf = state.whatIf ?? WHAT_IF_DEFAULTS;
  const [panelOpen, setPanelOpen] = useState(false);
  const maActive = maLocked || searchParams.get('layer') === 'ma';
  const whatIfActive = !isWhatIfDefault(whatIf);
  const silverPushOn = state.scenarios.includes('silver-push');
  const silverBundle = SCENARIOS.find((s) => s.id === 'silver-push');
  // The locked pane is measured: its width sets the zoom `fitView` settles on,
  // and in the flowsheet panel (from 1400 px) its height — the row's, which the
  // stage panel may set — is the room the layout stretches to fill.
  const paneRef = useRef<HTMLDivElement>(null);
  const [paneWidth, setPaneWidth] = useState<number | undefined>(undefined);
  const [paneHeight, setPaneHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const pane = paneRef.current;
    if (!maLocked || !pane || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect?.width) setPaneWidth(Math.round(rect.width));
      if (rect?.height) setPaneHeight(Math.round(rect.height));
    });
    observer.observe(pane);
    return () => observer.disconnect();
  }, [maLocked]);
  const lockedZoom = maLockedZoom(paneWidth);
  const fillHeight = maLocked && bare && paneHeight ? maLockedFillHeight(paneHeight, lockedZoom) : undefined;
  const view = useMemo(() => {
    const resolvedView = accountingView ?? periodWindows()[period];
    const base = maLocked
      ? metalAccountingProcessGraph(ranks, phase, samplePoints, state, lens, resolvedView, { fillHeight })
      : processGraph(
          ranks,
          phase,
          samplePoints,
          state,
          state.whatIf,
          false,
          lens,
          period,
        );
    // The locked graph's MA labels are already placed against its own layout.
    if (!maActive || maLocked) return base;
    const ma = metalAccountingLayer(resolvedView);
    const withMaOverlay = {
      ...base,
      // Nodes are taken exactly as `processGraph` built them. On the locked
      // route it writes every MA read — note, gap, suppression — *before*
      // `compactMaLockedGraphNodes` measures the card, so a field added here
      // would size a card from something it does not render (r11 MUST-FIX 1).
      // On the layer-toggled plant-model route (`?layer=ma`) it was called with
      // `maActive: false`, so the base already carries the constraint reads that
      // route keeps (paul-fmea.A13 / #278) and the MA layer writes there only
      // the strip and the edge labels.
      nodes: base.nodes,
      edges: maLocked && period === 'shift'
        ? overlayMetalAccountingOnEdges(base.edges, ma).map((edge) => ({
            ...edge,
            valueLabel: null,
          }))
        : overlayMetalAccountingOnEdges(base.edges, ma),
      maStrip: ma.strip,
    };
    return withMaOverlay;
  }, [ranks, phase, samplePoints, state, maActive, maLocked, lens, period, accountingView, whatIf, whatIfActive, fillHeight]);

  const toggleMaLayer = () => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (params.get('layer') === 'ma') params.delete('layer');
        else params.set('layer', 'ma');
        return params;
      },
      { replace: true },
    );
  };
  const scenarioCaption = whatIfActive
    ? WHAT_IF_CAPTION
    : silverPushOn && silverBundle?.description
      ? silverBundle.description
      : view.scenarioCaption;
  // polish.P11: on the plant model the GM band is the page's lead band,
  // drawn by the Scene above both views; only the MA-locked route keeps it here.
  const rollup = useMemo(
    () =>
      lens === 'gm' && maLocked
        ? gmRollupFromViews({ ...state, whatIf: null }, ranks, periodWindows())
        : null,
    [lens, state, ranks, maLocked],
  );

  const openPanel = () => {
    if (!onSetWhatIf) return;
    if (phase !== 'idle') onPhaseChange('idle');
    setPanelOpen(true);
  };
  const resetWhatIf = () => onSetWhatIf?.(null);
  // MA-locked height tracks the layout's natural (unstretched) height, never
  // the stretched one, so a pane that grows to its row cannot feed back into
  // its own minimum. Plant-model keeps its own constant.
  const canvasHeight =
    maLocked && view.maLockedHeight !== null
      ? maLockedCanvasHeight(view.maLockedHeight, lockedZoom)
      : GRAPH_CANVAS_H;
  const brackets = useMemo(
    () => (maLocked || !circuitCells ? undefined : circuitBrackets(circuitCells, view.nodes)),
    [maLocked, circuitCells, view.nodes],
  );

  return (
    <Commentable name="PlantStrip">
      <section
        className={cn(
          bare ? 'flex h-full min-h-0 flex-col gap-1' : 'space-y-1',
          !maLocked && 'card-matte-2 rounded-2xl px-3 pb-2.5 pt-2.5',
        )}
        data-process-graph
      >
        <div
          className={cn(
            'relative flex flex-wrap items-center gap-1.5',
            maLocked && hideMaToggle && 'hidden',
            !maLocked && 'justify-end',
          )}
        >
          {!maLocked ? (
          <>
          {/* polish.P11: the scenario phases and Push silver live in the page
              header now, so this toolbar keeps the graph's own controls. */}
          <button
            type="button"
            onClick={() => (panelOpen ? setPanelOpen(false) : openPanel())}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              whatIfActive || panelOpen
                ? 'border-ink-primary bg-ink-primary text-[var(--surface-2)]'
                : 'border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary hover:text-ink-primary',
            )}
          >
            Adjust inputs
          </button>
          </>
          ) : null}
          {!hideMaToggle ? (
            <button
              type="button"
              aria-pressed={maActive}
              aria-label="Metal accounting layer"
              onClick={toggleMaLayer}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                maActive
                  ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'border-[var(--glass-border)] bg-[var(--glass-surface)] text-muted hover:text-zinc-900 dark:hover:text-zinc-100',
              )}
            >
              <Layers className="h-3 w-3" aria-hidden />
              Metal accounting
            </button>
          ) : null}
          {!maLocked ? <p className="text-xs text-muted">{scenarioCaption}</p> : null}
          {panelOpen ? (
            <WhatIfPanel
              inputs={whatIf}
              onChange={(next) => onSetWhatIf?.(next)}
              onReset={resetWhatIf}
              onClose={() => setPanelOpen(false)}
            />
          ) : null}
        </div>

        {rollup && !hideRollup ? <RollupStrip view={rollup} /> : null}

        <div
          ref={paneRef}
          className={cn(
            'w-full overflow-hidden',
            bare
              ? 'min-h-0 flex-1'
              : 'rounded-2xl border border-[var(--glass-border)] bg-[color-mix(in_srgb,var(--glass-surface)_88%,transparent)]',
          )}
          style={{ minHeight: canvasHeight }}
          aria-label={`Process graph viewport ${GRAPH_VIEWBOX.width} by ${GRAPH_VIEWBOX.height}`}
        >
          <ReactFlowProvider>
            <ProcessGraphCanvas
              view={view}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              reducedMotion={reducedMotion}
              canvasHeight={canvasHeight}
              brackets={brackets}
              plantRoute={!maLocked}
              boundsTop={maLocked ? maLockedBoundsTop() : undefined}
              fill={bare}
              paneHeight={bare ? paneHeight : undefined}
            />
          </ReactFlowProvider>
        </div>

        {view.maStrip && !hideMaStrip ? (
          <MetalAccountingStrip
            view={view.maStrip}
            metalAccountingHref={
              hideMaStripLink ? undefined : projectSidebarHref(projectSlug, 'metal-accounting')
            }
            showChainGapCell={!hideMaStripChainGapCell}
            onSelectNode={onSelectNode}
          />
        ) : null}

        <p className={cn('mt-0 text-[10px] leading-tight text-muted', legendAside ? 'flex flex-wrap items-baseline justify-between gap-x-3' : '')}>
          <span>
            {maLocked
              ? 'Solid = forward flow · dashed = process-water network'
              : 'Solid = forward flow · arc atop a stage = internal recirculating load · dashed = process-water network'}
            {' · '}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-[var(--circuit-pb)]" aria-hidden />
              Pb circuit
            </span>
            {' · '}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-[var(--circuit-zn)]" aria-hidden />
              Zn circuit
            </span>
            {lens !== 'gm' ? (
              <>
                {' · '}
                <span className="inline-flex items-center gap-1">
                  <span aria-hidden style={{ color: 'var(--state-online)' }}>
                    ●
                  </span>
                  sample point
                </span>
              </>
            ) : null}
          </span>
          {legendAside ? <span className="tabular-nums">{legendAside}</span> : null}
        </p>
      </section>
    </Commentable>
  );
}
